import React, { useState, useRef } from 'react'
import { Modal, Button } from 'react-bootstrap'
import { KTIcon } from '../../../../../../../_metronic/helpers'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useAuth } from '../../../../../auth'
import axios from 'axios'
import { toast } from 'react-toastify'

interface Props {
    onBack: () => void
    selectedProcess: any
    inputMaterial: any[]
    outputMaterial: any[]
    productionDate: Date
    setProductionDate: (date: Date) => void
    actualProductionDate: Date
    setActualProductionDate: (date: Date) => void
    productionHours: number
    setProductionHours: (hours: number) => void
    productionCrew: number
    setProductionCrew: (crew: number) => void
    productionShift: number
    setProductionShift: (shift: number) => void
    productImage1: string
    setProductImage1: (image: string) => void
    productImage2: string
    setProductImage2: (image: string) => void
    handleSubmit: () => void
    isSubmitting: boolean
}

const OpsSummaryStep: React.FC<Props> = ({ onBack, selectedProcess, inputMaterial, outputMaterial, productionDate, setProductionDate, actualProductionDate, setActualProductionDate, productionHours, setProductionHours, productionCrew, setProductionCrew, productionShift, setProductionShift, productImage1, setProductImage1, productImage2, setProductImage2, handleSubmit, isSubmitting }) => {
    const { auth }: any = useAuth()
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [isUploading1, setIsUploading1] = useState(false)
    const [isUploading2, setIsUploading2] = useState(false)
    const fileInputRef1 = useRef<HTMLInputElement>(null)
    const fileInputRef2 = useRef<HTMLInputElement>(null)

    const handleConfirmSubmit = () => {
        setShowConfirmModal(false)
        handleSubmit()
    }

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, slot: number) => {
        const file = event.target.files?.[0]
        if (!file) return

        const formData = new FormData()
        formData.append('file', file)

        const setUploading = slot === 1 ? setIsUploading1 : setIsUploading2
        const setImageUrl = slot === 1 ? setProductImage1 : setProductImage2

        setUploading(true)
        try {
            const response = await axios.post(`${process.env.REACT_APP_BASE_API_URL}upload/image`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'app-id': '3',
                    'client-id': auth?.data?.clientId
                }
            })
            const imageUrl = response.data?.data?.id
            if (imageUrl) {
                setImageUrl(imageUrl)
                toast.success('Image uploaded successfully')
            } else {
                toast.error('Failed to get image URL from server')
            }
        } catch (err: any) {
            console.error('Image upload error:', err)
            toast.error(err?.response?.data?.message || 'Error uploading image')
        } finally {
            setUploading(false)
        }
    }
    return (
        <div className='card card-flush h-xl-100'>
            {/* Header */}
            <div className='card-header pt-7 min-h-auto'>
                <div className='card-title d-flex align-items-center'>
                    <button onClick={onBack} className='btn btn-icon btn-sm btn-active-light-primary me-3'>
                        <KTIcon iconName='arrow-left' className='fs-2' />
                    </button>
                    <h3 className='card-label fw-bolder text-dark fs-3 mb-0'>Summary</h3>
                </div>
                <div className='card-toolbar'>
                    <span className='text-success fw-bolder fs-4'>3</span>
                    <span className='text-dark fw-bold fs-6 ms-1'>of 3</span>
                </div>
            </div>

            <div className='card-body pt-5 px-5 pt-0'>
                {/* Production Info Card */}
                <div className='card border border-gray-300 rounded-4 mb-7 bg-light-light'>
                    <div className='card-body p-5'>
                        <div className='d-flex justify-content-between align-items-center mb-4'>
                            <h4 className='fw-bolder text-dark fs-4 mb-0'>Production</h4>
                            <span className='badge badge-light-warning fw-bolder px-4 py-2 rounded-pill'>Recycling</span>
                        </div>
                        <div className='d-flex flex-column gap-2'>
                            <div className='d-flex justify-content-between'>
                                <span className='text-gray-600 fw-bold'>Process</span>
                                <span className='text-dark fw-bolder'>{selectedProcess?.name}</span>
                            </div>
                            <div className='d-flex justify-content-between align-items-center'>
                                <span className='text-gray-600 fw-bold'>Date</span>
                                <DatePicker
                                    selected={productionDate}
                                    onChange={(date: Date | null) => date && setProductionDate(date)}
                                    className='form-control form-control-sm form-control-solid fw-bolder text-dark bg-transparent border-0 text-end p-0'
                                    dateFormat='dd/MM/yyyy'
                                    maxDate={new Date()}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='d-flex gap-5 w-100'>

                    {/* Input Material Card */}
                    <div className='card border border-gray-300 rounded-4 mb-7 w-50'>
                        <div className='card-body p-5 pt-3'>
                            <h4 className='fw-bolder text-dark fs-5 mb-4'>Input material</h4>
                            <div className='table-responsive'>
                                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                                    <thead>
                                        <tr className='bg-light text-dark fw-bolder fs-7  gs-0'>
                                            <th className='ps-4 min-w-100px rounded-start'>Category</th>
                                            <th className='min-w-100px text-center'>Item</th>
                                            <th className='min-w-80px text-end pe-4 rounded-end'>Quantity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inputMaterial?.filter((item: any) => item?.enteredValue > 0)?.map((material: any) => (
                                            <tr key={material.id} className='fs-6 fw-bold text-gray-800'>
                                                <td className='ps-4'>{material?.categoryName}</td>
                                                <td className='text-center'>{material?.name}</td>
                                                <td className='text-end pe-4'>{material?.enteredValue}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Output Material Card */}
                    <div className='card border border-gray-300 rounded-4 mb-7 w-50'>
                        <div className='card-body p-5 pt-3'>
                            <div className='d-flex justify-content-between align-items-center mb-4'>
                                <h4 className='fw-bolder text-dark fs-5 mb-0'>Output Material</h4>
                                <span className='badge badge-light-warning fw-bolder px-4 py-1 rounded-pill'>RECYCLABLE</span>
                            </div>
                            <div className='table-responsive'>
                                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                                    <thead>
                                        <tr className='bg-light text-dark fw-bolder fs-7  gs-0'>
                                            <th className='ps-4 min-w-100px rounded-start'>Category</th>
                                            <th className='min-w-100px text-center'>Item</th>
                                            <th className='min-w-80px text-end pe-4 rounded-end'>Quantity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {outputMaterial?.filter((item: any) => item?.enteredValue > 0)?.map((material: any) => (
                                            <tr key={material.id} className='fs-6 fw-bold text-gray-800'>
                                                <td className='ps-4'>{material?.categoryName}</td>
                                                <td className='text-center'>{material?.name}</td>
                                                <td className='text-end pe-4'>{material?.enteredValue}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className='text-dark fs-7 mt-2'>Note: All quantity in kg</div>
                        </div>
                    </div>
                </div>


                {/* Metadata Settings */}
                <div className='mb-7'>


                    <div className='row g-4'>
                        <div className='col-3'>
                            <label className='form-label fw-bolder text-gray-700 fs-6'>Actual Production Date</label>
                            <DatePicker
                                selected={actualProductionDate}
                                onChange={(date: Date | null) => date && setActualProductionDate(date)}
                                className='form-control form-control-solid rounded-4 w-100'
                                wrapperClassName='w-100'
                                dateFormat='dd/MM/yyyy'
                                maxDate={new Date()}
                            />
                        </div>
                        <div className='col-3'>
                            <label className='form-label fw-bolder text-gray-700 fs-6'>Operating Hours</label>
                            <input
                                type='number'
                                className='form-control form-control-solid rounded-4'
                                placeholder='Hours'
                                value={productionHours || ''}
                                onChange={(e) => setProductionHours(parseFloat(e.target.value) || 0)}
                            />
                        </div>
                        <div className='col-3'>
                            <label className='form-label fw-bolder text-gray-700 fs-6'>Crew Size</label>
                            <input
                                type='number'
                                className='form-control form-control-solid rounded-4'
                                placeholder='Nos'
                                value={productionCrew || ''}
                                onChange={(e) => setProductionCrew(parseInt(e.target.value) || 0)}
                            />
                        </div>
                        <div className='col-3 text-center'>
                            <label className='form-label fw-bolder text-gray-700 fs-6 d-block'>Shift</label>
                            <div className='btn-group w-100 border border-gray-300 rounded-4 overflow-hidden h-40px bg-white'>
                                {[1, 2, 3].map((shift) => (
                                    <button
                                        key={shift}
                                        onClick={() => setProductionShift(shift)}
                                        className={`btn btn-active-light-primary border-0 p-0 fs-6 fw-bold flex-grow-1 ${productionShift === shift ? 'btn-primary' : ''} ${shift < 3 ? 'border-end' : ''}`}
                                    >
                                        {shift}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chain of custody Section */}
                <div className='mb-10'>
                    <div className='bg-primary text-white p-4 d-flex align-items-center rounded-top-4 mb-4' style={{ backgroundColor: '#50cd89 !important' }}>
                        <KTIcon iconName='abstract-26' className='fs-2 text-white me-3' />
                        <span className='fw-bolder fs-5'>Chain of custody Information</span>
                    </div>

                    <div className='row g-5'>
                        <input
                            type='file'
                            ref={fileInputRef1}
                            style={{ display: 'none' }}
                            accept='image/*'
                            onChange={(e) => handleFileChange(e, 1)}
                        />
                        <input
                            type='file'
                            ref={fileInputRef2}
                            style={{ display: 'none' }}
                            accept='image/*'
                            onChange={(e) => handleFileChange(e, 2)}
                        />
                        <div className='col-6'>
                            <div
                                onClick={() => fileInputRef1.current?.click()}
                                className='border border-gray-300 border-dashed rounded-4 p-5 text-center bg-light cursor-pointer h-100 d-flex flex-column align-items-center justify-content-center min-h-125px overflow-hidden position-relative'
                            >
                                {isUploading1 ? (
                                    <div className='d-flex flex-column align-items-center'>
                                        <span className='spinner-border spinner-border-sm text-primary mb-2'></span>
                                        <span className='text-dark fs-8'>Uploading...</span>
                                    </div>
                                ) : productImage1 ? (
                                    <img src={productImage1} alt='Product 1' className='w-100 h-100 position-absolute' style={{ objectFit: 'cover' }} />
                                ) : (
                                    <>
                                        <div className='symbol symbol-40px symbol-circle bg-gray-200 mb-2 d-flex align-items-center justify-content-center'>
                                            <KTIcon iconName='camera' className='fs-2 text-gray-500' />
                                        </div>
                                        <span className='text-gray-800 fw-bold fs-8'>Product<br />Image *</span>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className='col-6'>
                            <div
                                onClick={() => fileInputRef2.current?.click()}
                                className='border border-gray-300 border-dashed rounded-4 p-5 text-center bg-light cursor-pointer h-100 d-flex flex-column align-items-center justify-content-center min-h-125px overflow-hidden position-relative'
                            >
                                {isUploading2 ? (
                                    <div className='d-flex flex-column align-items-center'>
                                        <span className='spinner-border spinner-border-sm text-primary mb-2'></span>
                                        <span className='text-dark fs-8'>Uploading...</span>
                                    </div>
                                ) : productImage2 ? (
                                    <img src={productImage2} alt='Product 2' className='w-100 h-100 position-absolute' style={{ objectFit: 'cover' }} />
                                ) : (
                                    <>
                                        <div className='symbol symbol-40px symbol-circle bg-gray-200 mb-2 d-flex align-items-center justify-content-center'>
                                            <KTIcon iconName='camera' className='fs-2 text-gray-500' />
                                        </div>
                                        <span className='text-gray-800 fw-bold fs-8'>Product<br />Image</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Button */}
                <div className='mb-10 mt-5 d-flex justify-content-center'>
                    <button
                        onClick={() => setShowConfirmModal(true)}
                        className='btn btn-dark rounded-pill py-4 w-250px fw-bolder fs-3 shadow-sm'
                        disabled={isSubmitting}
                    >
                        {!isSubmitting ? 'Save' : (
                            <span className='indicator-progress' style={{ display: 'block' }}>
                                Please wait...
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                        )}
                    </button>
                </div>

                {/* Confirmation Modal */}
                <Modal
                    show={showConfirmModal}
                    onHide={() => setShowConfirmModal(false)}
                    centered
                    backdrop='static'
                    className='rounded-4'
                >
                    <Modal.Header closeButton className='border-0 pt-7 px-9'>
                        <Modal.Title className='fw-bolder text-dark fs-2'>Confirm Submission</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className='px-9 py-5'>
                        <div className='text-gray-600 fs-5 fw-bold mb-2'>
                            Are you sure you want to submit this production data?
                        </div>
                        <div className='p-4 bg-light-primary rounded-3 border border-primary border-dashed'>
                            <div className='d-flex flex-column gap-1'>
                                <div className='d-flex justify-content-between'>
                                    <span className='text-gray-600 fw-bold fs-7'>Process:</span>
                                    <span className='text-dark fw-bolder fs-7'>{selectedProcess?.name}</span>
                                </div>
                                <div className='d-flex justify-content-between'>
                                    <span className='text-gray-600 fw-bold fs-7'>Total Output:</span>
                                    <span className='text-dark fw-bolder fs-7'>{outputMaterial?.reduce((acc, item) => acc + (item?.enteredValue || 0), 0)} kg</span>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className='border-0 pb-9 px-9 pt-0'>
                        <Button
                            variant='light'
                            onClick={() => setShowConfirmModal(false)}
                            className='btn btn-active-light-primary fw-bolder rounded-pill px-8'
                        >
                            No, Cancel
                        </Button>
                        <Button
                            variant='primary'
                            onClick={handleConfirmSubmit}
                            className='btn btn-primary fw-bolder rounded-pill px-8 shadow-sm'
                            style={{ backgroundColor: '#5e62ff' }}
                        >
                            Yes, Submit
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    )
}

export default OpsSummaryStep